import React, { useEffect, useState, useMemo } from "react";
import Loading from "../Loading";
import "./Modal.css";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Select from "react-select";
import { fetchAllProviders } from "../../config/LiveApi";
import { useSelector } from "react-redux";

const Modal = ({
  onClose,
  onSubmit,
  initialFormData,
  isUpdating,
  isLoading,
  role,
}) => {
  const token = useSelector((state) => state.userReducer.token);
  const [providersName, setProvidersName] = useState([]);
  const [selectedRole, setSelectedRole] = useState(role);
  const [formData, setFormData] = useState(initialFormData);

  const handleSelectChangePayin = (selectedOption) => {
    if (selectedOption) {
      const { name, value } = selectedOption;

      const matchingProvider = providersName.find((item) => item.id === value);
      const providerPayinComission = matchingProvider
        ? Number(matchingProvider.providerPayinComm)
        : 0;

      setFormData((prev) => ({
        ...prev,
        [name]: value,
        providerPayinComission: providerPayinComission.toString(),
      }));

      const updatedAdminPayinComission =
        Number(formData.totalPayinComission) - Number(providerPayinComission);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        adminPayinComission: updatedAdminPayinComission.toString(),
      }));
    }
  };

  const handleSelectChangePayout = (selectedOption) => {
    if (selectedOption) {
      const { name, value } = selectedOption;

      const matchingProvider = providersName.find((item) => item.id === value);
      const providerPayoutComission = matchingProvider
        ? Number(matchingProvider.providerPayoutComm)
        : 0;

      setFormData((prev) => ({
        ...prev,
        [name]: value,
        providerPayoutComission: providerPayoutComission.toString(),
      }));

      const updatedAdminPayoutComission =
        Number(formData.totalPayoutComission) - Number(providerPayoutComission);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        adminPayoutComission: updatedAdminPayoutComission.toString(),
      }));
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const fetchProviders = async () => {
      if (role === "admin" || role === "subAdmin") return;

      try {
        const response = await fetchAllProviders(token);
        const providerName = response.data.providers;

        setProvidersName(providerName);
      } catch (error) {
        console.error("Error fetching providers:", error);
      }
    };

    fetchProviders();
  }, [role, token]);

  const defaultFormData = {
    providerPayinComission:
      role === "admin" ? 0 : formData?.providerPayinComission,
    providerPayoutComission:
      role === "admin" ? 0 : formData?.providerPayoutComission,
    adminPayinComission: role === "admin" ? 0 : formData?.adminPayinComission,
    adminPayoutComission: role === "admin" ? 0 : formData?.adminPayoutComission,
    rollingReserve: role === "admin" ? 0 : formData?.rollingReserve,
    totalPayinComission: role === "admin" ? 0 : formData?.totalPayinComission,
    totalPayoutComission: role === "admin" ? 0 : formData?.totalPayoutComission,
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleRoleChange = (e) => {
    const { value } = e.target;
    setSelectedRole(value);
  };

  const payinOptions = useMemo(() => {
    return providersName
      .filter(
        (p) =>
          (p.type === "payin" || p.type === "both") &&
          p.mode !== "UPI" &&
          p.isActive,
      )
      .map((p) => ({
        label: (
          <span>
            {p.providerName} - {p.orgName}
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: p.isActive ? "green" : "red",
                marginLeft: 8,
                verticalAlign: "middle",
              }}
            />
          </span>
        ),
        searchLabel: `${p.providerName} - ${p.orgName}`,
        value: p.id,
        name: "payinProviderID",
      }));
  }, [providersName]);

  const payoutOptions = useMemo(() => {
    return providersName
      .filter(
        (p) =>
          (p.type === "payout" || p.type === "both") &&
          p.mode !== "UPI" &&
          p.isActive,
      )
      .map((p) => ({
        label: (
          <span>
            {p.providerName} - {p.orgName}
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: p.isActive ? "green" : "red",
                marginLeft: 8,
                verticalAlign: "middle",
              }}
            />
          </span>
        ),
        searchLabel: `${p.providerName} - ${p.orgName}`,
        value: p.id,
        name: "payoutProviderID",
      }));
  }, [providersName]);

  const refundOptions = useMemo(() => {
    return providersName
      .filter(
        (p) =>
          (p.type === "payout" || p.type === "both") &&
          p.mode === "UPI" &&
          p.isActive,
      )
      .map((p) => ({
        label: `${p.providerName} - ${p.orgName}`,
        value: p.id,
        name: "refundProviderID",
      }));
  }, [providersName]);

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal">
        <h3>{!isUpdating ? "Add User" : "Update User"}</h3>
        {isLoading ? (
          <Loading />
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit(formData);
            }}
          >
            {(!isUpdating || (isUpdating && formData.role !== "admin")) && (
              <>
                <p className="label-data">Name</p>
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={formData?.name}
                  onChange={handleInputChange}
                />
              </>
            )}
            {!isUpdating && (
              <>
                <p className="label-data">Password</p>
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={formData?.password || ""}
                  onChange={handleInputChange}
                />
              </>
            )}
            {/* //alias */}
            {(role === "super" ||
              (role === "subSuper" && formData.role === "admin") ||
              role === "operation") && (
              <>
                <p className="label-data">Alias</p>
                <input
                  type="text"
                  name="alias"
                  placeholder="Alias"
                  value={formData?.alias || ""}
                  onChange={handleInputChange}
                />
              </>
            )}
            <p className="label-data">Phone Number</p>
            <input
              type="text"
              name="phoneNumber"
              placeholder="Phone Number"
              value={formData?.phoneNumber}
              onChange={handleInputChange}
            />
            {!isUpdating && (
              <>
                <p className="label-data">Email</p>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData?.email}
                  onChange={handleInputChange}
                />
              </>
            )}
            {role === "admin" && !isUpdating && (
              <FormControl>
                <p className="label-data">User Role</p>
                <RadioGroup
                  name="role"
                  value={formData?.role}
                  onChange={(e) => {
                    handleRoleChange(e);
                    handleInputChange(e);
                  }}
                >
                  <FormControlLabel
                    value="subAdmin"
                    control={<Radio />}
                    label="Sub-Admin"
                  />
                  <FormControlLabel
                    value="mops"
                    control={<Radio />}
                    label="Mops"
                  />
                </RadioGroup>
              </FormControl>
            )}
            {(role === "super" || role === "subSuper") && !isUpdating && (
              <FormControl>
                <p className="label-data">User Role</p>
                <RadioGroup
                  name="role"
                  value={formData?.role}
                  onChange={(e) => {
                    handleRoleChange(e);
                    handleInputChange(e);
                  }}
                >
                  <FormControlLabel
                    value="admin"
                    control={<Radio />}
                    label="Admin"
                  />
                  <FormControlLabel
                    value="operation"
                    control={<Radio />}
                    label="Operation"
                  />
                  {role === "super" && !isUpdating && (
                    <FormControlLabel
                      value="subSuper"
                      control={<Radio />}
                      label="Sub-Super"
                    />
                  )}
                  <FormControlLabel
                    value="pros"
                    control={<Radio />}
                    label="PROS"
                  />
                  <FormControlLabel
                    value="accounts"
                    control={<Radio />}
                    label="Accounts"
                  />
                  <FormControlLabel
                    value="fundLoad"
                    control={<Radio />}
                    label="FundLoad"
                  />
                  <FormControlLabel
                    value="compliance"
                    control={<Radio />}
                    label="Compliance"
                  />
                  <FormControlLabel
                    value="trafficManager"
                    control={<Radio />}
                    label="Traffic Manager"
                  />
                  <FormControlLabel
                    value="reseller"
                    control={<Radio />}
                    label="Reseller"
                  />
                </RadioGroup>
              </FormControl>
            )}
            {(((role === "super" || role === "subSuper") &&
              formData.role === "admin") ||
              role === "operation") && (
              <>
                <p className="label-data">Total Payin Comission</p>
                <input
                  type="text"
                  name="totalPayinComission"
                  placeholder="Total Payin Comission"
                  value={defaultFormData.totalPayinComission}
                  onChange={handleInputChange}
                />

                <p className="label-data">Total Payout Comission</p>
                <input
                  type="text"
                  name="totalPayoutComission"
                  placeholder="Total Payout Comission"
                  value={defaultFormData.totalPayoutComission}
                  onChange={handleInputChange}
                />

                <p className="label-data">API Version</p>
                <RadioGroup
                  name="api_version"
                  value={formData.api_version?.toString()}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      api_version: parseInt(e.target.value),
                    })
                  }
                  row
                >
                  <FormControlLabel value="1" control={<Radio />} label="V1" />
                  <FormControlLabel
                    value="2"
                    control={<Radio />}
                    label="V2 (AES Encrypted)"
                  />
                </RadioGroup>

                <p className="label-data">Payin Provider Name</p>
                <Select
                  name="payinProviderID"
                  value={
                    payinOptions.find(
                      (option) => option.value === formData?.payinProviderID,
                    ) || null
                  }
                  onChange={handleSelectChangePayin}
                  options={payinOptions}
                  formatOptionLabel={(option) => option.label}
                  getOptionLabel={(option) => option.searchLabel}
                  getOptionValue={(option) => option.value}
                />

                <p className="label-data">Payout Provider Name</p>
                <Select
                  name="payoutProviderID"
                  value={
                    payoutOptions.find(
                      (option) => option.value === formData?.payoutProviderID,
                    ) || null
                  }
                  onChange={handleSelectChangePayout}
                  options={payoutOptions}
                  formatOptionLabel={(option) => option.label}
                  getOptionLabel={(option) => option.searchLabel}
                  getOptionValue={(option) => option.value}
                />

                <p className="label-data">Refund Provider Name</p>
                <Select
                  name="refundProviderID"
                  value={
                    refundOptions.find(
                      (option) => option.value === formData?.refundProviderID,
                    ) || null
                  }
                  onChange={handleSelectChangePayout}
                  options={refundOptions}
                />

                <p className="label-data">Provider Payin Comission</p>
                <input
                  type="text"
                  name="providerPayinComission"
                  placeholder="Provider PayIn Comission"
                  value={defaultFormData.providerPayinComission}
                  onChange={handleInputChange}
                  readOnly
                />

                <p className="label-data">Provider Payout Comission</p>
                <input
                  type="text"
                  name="providerPayoutComission"
                  placeholder="Provider PayOut Comission"
                  value={defaultFormData.providerPayoutComission}
                  onChange={handleInputChange}
                  readOnly
                />

                <p className="label-data">Super Payin Comission</p>
                <input
                  type="text"
                  name="adminPayinComission"
                  placeholder="Admin PayIn Comission"
                  value={parseFloat(
                    defaultFormData.adminPayinComission || 0,
                  ).toFixed(3)}
                  onChange={handleInputChange}
                  readOnly
                />

                <p className="label-data">Super Payout Comission</p>
                <input
                  type="text"
                  name="adminPayoutComission"
                  placeholder="Admin PayOut Comission"
                  value={parseFloat(
                    defaultFormData.adminPayoutComission || 0,
                  ).toFixed(3)}
                  onChange={handleInputChange}
                  readOnly
                />

                <p className="label-data">Minimum Payin Amount</p>
                <input
                  type="number"
                  min="1"
                  name="minPayinAmount"
                  placeholder="Minimum Payin Amount"
                  value={formData.minPayinAmount}
                  onChange={handleInputChange}
                />

                <p className="label-data">Maximum Payin Amount</p>
                <input
                  type="number"
                  min="1"
                  name="maxPayinAmount"
                  placeholder="Maximum Payin Amount"
                  value={formData.maxPayinAmount}
                  onChange={handleInputChange}
                />

                <p className="label-data">Minimum Payout Amount</p>
                <input
                  type="number"
                  min="1"
                  name="minPayoutAmount"
                  placeholder="Minimum Payout Amount"
                  value={formData.minPayoutAmount}
                  onChange={handleInputChange}
                />

                <p className="label-data">Maximum Payout Amount</p>
                <input
                  type="number"
                  min="1"
                  name="maxPayoutAmount"
                  placeholder="Maximum Payout Amount"
                  value={formData.maxPayoutAmount}
                  onChange={handleInputChange}
                />

                <p className="label-data">Rolling Reserve (%)</p>
                <input
                  type="text"
                  name="rollingReserve"
                  placeholder="Rolling Reserve"
                  value={defaultFormData.rollingReserve}
                  onChange={handleInputChange}
                />

                <p className="label-data">Rolling Reserve Days</p>
                <input
                  type="text"
                  name="rollingReserveDays"
                  placeholder="Rolling Reserve Days"
                  value={formData.rollingReserveDays}
                  onChange={handleInputChange}
                />

                <p className="label-data">Penalty Amount</p>
                <input
                  type="text"
                  name="penaltyAmount"
                  placeholder="Rolling Reserve Days"
                  value={formData.penaltyAmount}
                  onChange={handleInputChange}
                />
                <p className="label-data">Payout Access</p>
                <RadioGroup
                  name="payoutFormAccess"
                  value={formData.payoutFormAccess ? "true" : "false"}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      payoutFormAccess: e.target.value === "true",
                    })
                  }
                  row
                >
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label="No"
                  />
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label="Yes"
                  />
                </RadioGroup>
              </>
            )}

            <div className="modal-buttons">
              <button type="submit">{isUpdating ? "Update" : "Add"}</button>
              <button type="button" onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Modal;
